import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Radar, Phone } from 'lucide-react';
import { toast } from 'sonner';
import RegPlate from './RegPlate';

const ORANGE = '#ee7203';
const PURPLE = '#a855f7';

/**
 * New Transport UI — the vehicle header at the top of a vehicle's run table: a slim, full-width header in the same white
 * "liquid glass" as the board around the vehicle's day (dark glass while the Global Header is dark — the colours come from
 * the `.c30-tr-board` / `.c30-tr-header` CSS in TransportNewUI.jsx, so the writing is black on white and white on dark).
 * It holds the registration drawn as a real number plate (full registration when there is room, otherwise the last
 * three characters, still as a plate), the driver's name with their phone number beside it, and the Moffet pill
 * (white with orange writing) on the right.
 *
 * Clicking anywhere on the header opens the Edit Vehicle modal (the Moffet dropdown, the phone number and the Track
 * button stop the click from bubbling). `trackingUrl` (Settings > Vehicles > Live Tracking Link) adds a purple "Track"
 * pill.
 */
const DriverHeaderBar = ({ vehicle, onEdit, editable = false, onMoffetChange, trackingUrl, onOpenTracking }) => {
  const barRef = useRef(null);
  const nameRef = useRef(null);
  const [shortReg, setShortReg] = useState(false);

  // Full registration while there is room; if the header overflows or the driver's name has to be cut off, fall back
  // to the last three characters (same plate design). It is measured again whenever the header's width changes.
  useLayoutEffect(() => {
    if (shortReg) return;
    const bar = barRef.current;
    const name = nameRef.current;
    const barOverflows = bar && bar.scrollWidth > bar.clientWidth + 1;
    const nameCut = name && name.scrollWidth > name.clientWidth + 1;
    if (barOverflows || nameCut) setShortReg(true);
  });
  useEffect(() => {
    const bar = barRef.current;
    if (!bar || typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = bar.clientWidth;
    const observer = new ResizeObserver(() => {
      if (Math.abs(bar.clientWidth - lastWidth) > 2) { lastWidth = bar.clientWidth; setShortReg(false); }
    });
    observer.observe(bar);
    return () => observer.disconnect();
  }, []);

  if (!vehicle) return null;
  const fullName = vehicle.driver_name || 'No Driver';
  const hasMoffet = vehicle.moffet_override != null ? vehicle.moffet_override : !!vehicle.has_moffet;
  // No "back at base" pill here any more: the green BACK AT BASE tab on the last drop and the base bars on the entries show it.

  return (
    <div
      ref={barRef}
      onClick={() => onEdit?.(vehicle)}
      className={`flex items-center flex-nowrap gap-2 px-3 py-1.5 overflow-hidden ${onEdit ? 'cursor-pointer' : ''}`}
      style={{ color: 'var(--tr-ink, #1d1d1b)' }}
      data-testid="transport-new-driver-header"
      title={onEdit ? 'Click to edit vehicle details' : undefined}
    >
      <RegPlate registration={vehicle.vehicle_registration} short={shortReg} size="md" data-testid="transport-new-header-plate" />
      <span ref={nameRef} className="text-[13px] font-bold truncate flex-shrink min-w-0" title={fullName}>{fullName}</span>
      {vehicle.driver_phone && (
        <span
          data-testid={`transport-new-header-phone-${vehicle.id}`}
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard?.writeText(vehicle.driver_phone);
            toast.success('Phone number copied');
          }}
          className="flex items-center gap-1 hover:underline text-[11px] font-semibold flex-shrink-0 cursor-pointer"
          style={{ color: 'var(--tr-ink-soft, rgba(29,29,27,0.62))' }}
          title="Click to copy the driver's number"
        >
          <Phone className="w-3 h-3" />{vehicle.driver_phone}
        </span>
      )}
      {trackingUrl && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpenTracking?.(vehicle, trackingUrl); }}
          className="flex-shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full text-white text-[10px] font-bold shadow-sm hover:brightness-110 transition-all"
          style={{ backgroundColor: PURPLE }}
          title="Track this vehicle"
          data-testid={`transport-new-tracking-btn-${vehicle.id}`}
        >
          <Radar className="w-3 h-3" />
          Track
        </button>
      )}
      <div className="flex-1 min-w-[2px]" />
      {editable ? (
        <select
          data-testid={`transport-new-moffet-select-${vehicle.id}`}
          value={hasMoffet ? 'yes' : 'no'}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onMoffetChange?.(vehicle, e.target.value === 'yes')}
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white cursor-pointer outline-none flex-shrink-0"
          style={{ color: ORANGE, border: `1px solid ${ORANGE}` }}
          title="Has Moffet today?"
        >
          <option value="no">-</option>
          <option value="yes">Moffet</option>
        </select>
      ) : (
        hasMoffet && (
          <span
            className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-white flex-shrink-0"
            style={{ color: ORANGE, border: `1px solid ${ORANGE}` }}
            data-testid={`transport-new-moffet-pill-${vehicle.id}`}
          >
            Moffet
          </span>
        )
      )}
    </div>
  );
};

export default DriverHeaderBar;
